"use client";

import EmailIcon from "@/icons/email";
import { BASE_URL, CALLBACK_URL, SIGNIN_API } from "@/lib/constants";
import { cn } from "@/lib/utils";
import { Loader2Icon } from "lucide-react";
import { signIn } from "next-auth/react";
import { usePathname, useSearchParams } from "next/navigation";
import { useState } from "react";
import toast from "react-hot-toast";
import OTPInput from "react-otp-input";

const SignInForm = () => {
	const queryParams = useSearchParams();
	const currentPath = usePathname();

	const [code, setCode] = useState<string>("");
	const [email, setEmail] = useState<string>("");
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [showVerificationStep, setShowVerificationStep] = useState<boolean>(false);

	const sendOTP = async (e: React.FormEvent) => {
		e.preventDefault();
		setIsLoading(true);

		const formattedEmail = email.toLowerCase();
		const validateEmailResponse = await fetch(SIGNIN_API, {
			method: "POST",
			body: JSON.stringify({ email: formattedEmail }),
		});

		if (validateEmailResponse.ok) {
			const signInResponse = await signIn("email", {
				email: formattedEmail,
				redirect: false,
			});

			if (signInResponse?.ok) {
				setShowVerificationStep(true);
				toast.success("OTP sent to your email");
			} else {
				toast.error("Something went wrong during sign in, please try again");
			}
		} else {
			const { message } = await validateEmailResponse.json();
			toast.error(message);
		}

		setIsLoading(false);
	};

	const handleLogin = async () => {
		if (code.length !== 6) {
			toast.error("Enter a valid OTP");
			return;
		}

		setIsLoading(true);
		const formattedEmail = email.toLowerCase();
		const verificationUrl = new URL(`${BASE_URL}/api/auth/callback/email`);
		verificationUrl.searchParams.set("email", formattedEmail);
		verificationUrl.searchParams.set("token", code);

		if (CALLBACK_URL) {
			verificationUrl.searchParams.set("callbackUrl", CALLBACK_URL);
		}

		const verificationResponse = await fetch(verificationUrl);
		if (verificationResponse.ok) {
			if (currentPath === "/signin") {
				let url = "/";

				const next = queryParams.get("next");

				// Redirect to next destination if valid
				if (next && next.startsWith("/") && !next.startsWith("//")) {
					url = next;
				}

				window.location.href = url;
			} else {
				window.location.reload();
			}
		} else {
			toast.error("OTP verification failed, please retry");
		}

		setIsLoading(false);
	};

	return (
		<>
			{!showVerificationStep ? (
				<form onSubmit={sendOTP}>
					<div className="mb-4">
						<label htmlFor="email" className="mb-2 block text-sm text-gray-600">
							Enter your email
						</label>

						<div className="flex items-center rounded-md border border-form-field p-2 transition focus-within:border-blue-500">
							<span>
								<EmailIcon className="h-5 w-5" />
							</span>
							<input
								id="email"
								type="email"
								name="email"
								required
								placeholder="Enter your email"
								onChange={(e) => setEmail(e.target.value)}
								className="w-full bg-transparent px-2 text-sm lowercase outline-none transition-all duration-300 ease-in-out placeholder:normal-case"
							/>
						</div>
					</div>

					<button
						type="submit"
						className={cn(
							"flex w-full items-center justify-center gap-1 rounded-md px-8 py-2 text-sm font-semibold transition",
							isLoading
								? "cursor-not-allowed bg-gray-200 text-gray-500"
								: "bg-brand-primary text-white"
						)}
					>
						{isLoading ? (
							<div>
								<Loader2Icon className="h-4 w-4 animate-spin stroke-gray-600" />
							</div>
						) : null}
						<span>{isLoading ? "Sending OTP" : "Login"}</span>
					</button>
				</form>
			) : (
				<>
					<div className="mb-4">
						<label htmlFor="otp" className="mb-2 block text-sm">
							Enter OTP
							<p className="text-xxs text-gray-400">Please enter the OTP sent to your email</p>
						</label>

						<div className="flex items-center justify-center rounded bg-brand-primary/10 px-4 py-8">
							<OTPInput
								value={code}
								numInputs={6}
								shouldAutoFocus
								onChange={setCode}
								inputType="tel"
								containerStyle="gap-1 flex"
								inputStyle="border rounded-md border-[#B0CAF6] focus:outline-brand-primary text-brand-primary basis-full sm:py-2 text-sm sm:text-lg w-10 h-10 sm:w-14 sm:h-14 text-center"
								renderInput={(props, index) => (
									<>
										<input
											{...props}
											placeholder="0"
											style={{
												appearance: "none",
												MozAppearance: "textfield",
												WebkitAppearance: "none",
											}}
										/>

										{index === 2 && (
											<span className="mx-0.5 text-xl font-bold text-gray-400 sm:mx-2">-</span>
										)}
									</>
								)}
							/>
						</div>
					</div>

					<button
						onClick={handleLogin}
						className={cn(
							"flex w-full items-center justify-center gap-1 rounded-md px-2 py-2 text-sm font-semibold transition",
							isLoading
								? "cursor-not-allowed bg-gray-200 text-gray-500"
								: "bg-brand-primary text-white"
						)}
					>
						{isLoading ? (
							<div>
								<Loader2Icon className="h-4 w-4 animate-spin stroke-gray-600" />
							</div>
						) : null}
						<span>{isLoading ? "Verifying OTP" : "Login"}</span>
					</button>
				</>
			)}
		</>
	);
};

export default SignInForm;
